import {createSelector, createSlice, isAnyOf} from "@reduxjs/toolkit";
import {enhancedNavigationAPI} from "~/api/enhancedNavigationAPI";
import {NAVIGATION_STATUS_INITIAL, NAVIGATION_STATUS_LOADED, NAVIGATION_STATUS_LOADING} from "~/constants/Navigation";
import {sanitizePath} from "~/lib";
import {locationChange} from "~/reducers/app";

import config, {Slug} from "~/config";

import type {NavigationInitialState, NavigationMenu, NavigationSlugInfo} from "~/reducers/navigation.types";
import type {ConfigRoutes} from "~/config.types";
import {LinkProps} from "~/components/common/Link.props";
import {useSelector} from "react-redux";

const prepareSlugInfo = (pages: ConfigRoutes, prefixSlug: string = "", prefixPath: string = ""): NavigationSlugInfo => {
    return Object.keys(pages).reduce<NavigationSlugInfo>(
        (slugs, slug) => {
            const page = pages[slug];
            const path = sanitizePath([
                prefixPath,
                page.path || slug
            ].join("/"));

            const prefixedSlug = prefixSlug ? `${prefixSlug}-${slug}` : slug;
            slugs[prefixedSlug] = {
                path,
                title: page.title,
                ico: page.ico,
            };
            if (page.children) {
                slugs = {
                    ...slugs,
                    ...(prepareSlugInfo(page.children, prefixedSlug, path))
                };
            }

            return slugs;
        },
        {}
    );
};

const initialState: NavigationInitialState = {
    status: null,
    slugInfo: prepareSlugInfo(config.routes),
    menus: {},
    current: null
};

export const navigationSlice = createSlice({
    name: "navigation",
    initialState,
    reducers: {
        init: (state) => ({
            ...state,
            status: NAVIGATION_STATUS_INITIAL,
        }),
    },
    extraReducers: builder => {
        builder.addCase(
            locationChange,
            (state, action) => ({
                ...state,
                current: action.payload.page
            })
        );
        builder.addMatcher(
            isAnyOf(enhancedNavigationAPI.endpoints.getNavigationMenus.matchPending),
            (state) => ({
                ...state,
                status: NAVIGATION_STATUS_LOADING
            })
        );
        builder.addMatcher(
            enhancedNavigationAPI.endpoints.getNavigationMenus.matchRejected,
            (state) => {
                return {
                    ...state,
                    status: NAVIGATION_STATUS_INITIAL,
                };
            }
        );
        builder.addMatcher(
            enhancedNavigationAPI.endpoints.getNavigationMenus.matchFulfilled,
            (state, {payload}) => ({
                ...state,
                status: NAVIGATION_STATUS_LOADED,
                menus: {
                    ...state.menus,
                    ...(payload.menu || {})
                }
            })
        );
    },
    selectors: {
        getNavigationStatus: state => state.status,
        getMenu: createSelector(
            state => state.menus,
            menus => (name: string): NavigationMenu => menus[name] || {}
        ),
        getSlugInfo: createSelector(
            state => state.slugInfo,
            slugInfo => (slug: string | undefined, defSlug?: string): NavigationSlugInfo[string] => slug && slugInfo[slug] || defSlug && slugInfo[defSlug] || {path: null}
        ),
    }
});

export function useLoginWithRetURL(): LinkProps["withRetURL"] {
    const slugSelector = useSelector(getSlugInfo);
    const {path: signupSlugPath} = slugSelector(Slug.Signup);
    const {path: loginSlugPath} = slugSelector(Slug.Login);
    const {path: logoutSlugPath} = slugSelector(Slug.Logout);
    const {path: oauthSlugPath} = slugSelector(Slug.Oauth);

    return (toURLScheme, location, dryRun = false) => {
        if (dryRun) {
            return true;
        }
        const locationPathname = location && location.pathname || "";

        return toURLScheme.pathname !== locationPathname
            && ![
                signupSlugPath,
                loginSlugPath,
                logoutSlugPath
            ].includes(locationPathname)
            && !locationPathname.startsWith(oauthSlugPath);
    };
}

export const {init} = navigationSlice.actions;

export const {getNavigationStatus, getSlugInfo, getMenu} = navigationSlice.selectors;

export default navigationSlice;
